import { CreditTxTypeEnum, CreditTxWithMeta, PurchaseStatusEnum } from "@humanex/interfaces";
import { Button } from "@humanex/ui/components";
import { useI18n } from "@humanex/ui/libs";
import { Coins, HandCoins, ShoppingCart, Star, TicketCheck } from "lucide-react";
import { useMemo } from "react";

// Transaction component
interface CreditTxItemProps {
  transaction: CreditTxWithMeta;
  className?: string;
  onPaymentClick: (transaction: CreditTxWithMeta) => void;
}

const TX_CFGS: Record<CreditTxTypeEnum, { icon: any; iconClassName: string; text: string }> = {
  PAYMENT: {
    icon: TicketCheck,
    iconClassName: "bg-secondary-400 text-white",
    text: "credits.transactionStatuses.pending"
  },
  PURCHASE: {
    icon: ShoppingCart,
    iconClassName: "bg-success-DEFAULT text-white",
    text: "credits.transactionStatuses.pending"
  },
  USAGE: {
    icon: TicketCheck,
    iconClassName: "bg-warning-400 text-white",
    text: "credits.transactionStatuses.pending"
  },
  REFERRAL: {
    icon: Star,
    iconClassName: "bg-success-DEFAULT text-white",
    text: "credits.transactionStatuses.pending"
  },
  BONUS: {
    icon: Coins,
    iconClassName: "bg-success-DEFAULT text-white",
    text: "credits.transactionStatuses.pending"
  },
  ACTIVATION: {
    icon: Coins,
    iconClassName: "bg-success-DEFAULT text-white",
    text: "credits.transactionStatuses.pending"
  },
  APPLICATION: {
    icon: Coins,
    iconClassName: "bg-success-DEFAULT text-white",
    text: "credits.transactionStatuses.pending"
  },
  APPLICATION_REWARD: {
    icon: HandCoins,
    iconClassName: "bg-success-DEFAULT text-white",
    text: "credits.transactionStatuses.pending"
  }
};

export const CreditTxItem = ({ transaction, className, onPaymentClick }: CreditTxItemProps) => {
  const { t, formatCredit, formatDate } = useI18n();

  const txCfg = useMemo(() => TX_CFGS[transaction.type], [transaction.type]);

  const amountSign =
    transaction.type === CreditTxTypeEnum.PAYMENT
      ? ""
      : transaction.type === CreditTxTypeEnum.PURCHASE ||
          transaction.type === CreditTxTypeEnum.BONUS ||
          transaction.type === CreditTxTypeEnum.REFERRAL ||
          transaction.type === CreditTxTypeEnum.APPLICATION_REWARD
        ? "+"
        : "-";

  // Helper function to get payment status text and determine if button should be shown
  const getPaymentStatusInfo = () => {
    // Only purchase transactions have payment status
    if (transaction.type !== CreditTxTypeEnum.PURCHASE && transaction.type !== CreditTxTypeEnum.PAYMENT) {
      return { text: "", showButton: false };
    }

    if (transaction.purchaseMeta?.status === PurchaseStatusEnum.SUCCESS) {
      return { text: t("credits.transactionStatuses.paid"), showButton: false };
    }
    if (transaction.purchaseMeta?.status === PurchaseStatusEnum.FAILED) {
      return {
        text: t("credits.transactionStatuses.cancelled"),
        showButton: false
      };
    }
    if (transaction.purchaseMeta?.status === PurchaseStatusEnum.PENDING) {
      // Check if pending transaction has expired
      if (transaction.purchaseMeta?.expiresAt) {
        const now = new Date();
        const expiresAt = new Date(transaction.purchaseMeta?.expiresAt);
        if (now > expiresAt) {
          return {
            text: t("credits.transactionStatuses.expired"),
            showButton: false
          };
        }
      }
      return {
        text: t("credits.transactionStatuses.pending"),
        showButton: true
      };
    }
    // Default case - assume pending if no status
    return { text: t("credits.transactionStatuses.pending"), showButton: true };
  };

  const paymentStatusInfo = getPaymentStatusInfo();

  return (
    <div className={`flex flex-col items-center gap-2 w-full py-3 px-1 ${className}`}>
      <div className="flex items-center gap-2 w-full justify-between">
        <div className="flex items-center">
          <div className={`${txCfg.iconClassName} p-1.5 rounded-md`}>
            <txCfg.icon className="w-6 h-6" strokeWidth={2} />
          </div>
        </div>
        <div className="flex flex-1 items-center justify-between">
          <div className="flex-1 flex flex-col">
            <div className="flex items-center justify-start gap-2">
              <span className="text-sm sm:text-base font-normal text-ink">
                {amountSign}
                {formatCredit(transaction.amount)}
              </span>
              {transaction.type === CreditTxTypeEnum.PURCHASE ||
                (transaction.type === CreditTxTypeEnum.PAYMENT && paymentStatusInfo.text && (
                  <span className="text-xs sm:text-sm font-medium text-gray-500 bg-gray-50 rounded-md px-3 py-1">
                    {paymentStatusInfo.text}
                  </span>
                ))}
            </div>
            <p className="text-xs sm:text-sm font-normal text-gray-500">{transaction.desc}</p>
          </div>

          <div className="text-end">
            <p className="text-xs sm:text-sm font-normal text-gray-500">
              {formatDate(new Date(transaction.createdAt))}
            </p>
            {paymentStatusInfo.showButton && (
              <Button
                variant="outline"
                padding="px-4 py-1.5 mt-2"
                className="text-sm"
                onClick={() => onPaymentClick(transaction)}
              >
                {t("credits.payNow")}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
